import moment from 'moment';

import type {AuthorProfileQuery} from '../AuthorDashboard.queries.generated';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface MonthBucket {
  count: number;
  easy: number;
  moderate: number;
  difficult: number;
}

export type PublishedQuestionDates = Record<string, MonthBucket>;

export interface QuestionDifficulties {
  all: number;
  easy: number;
  moderate: number;
  difficult: number;
}

export type QuestionTypeCounts = ReturnType<typeof createTypeObject>;

/** The author record the dashboard header and the metrics subject filter read. */
export type AuthorProfile = NonNullable<AuthorProfileQuery['user']>;

/** A published question, as much of it as the metrics panel reads. */
export interface MetricsQuestion {
  publishedAt?: string | null;
  difficulty?: number | null;
  questionType?: string | null;
}

export function createYearObject(): PublishedQuestionDates {
  return MONTHS.reduce<PublishedQuestionDates>((acc, curr) => {
    acc[curr] = {
      count: 0,
      easy: 0,
      moderate: 0,
      difficult: 0
    };
    return acc;
  }, {});
}

export function createTypeObject() {
  return {
    multipleChoice: 0,
    all: 0,
    freeResponse: 0,
    twoWay: 0,
    passageCorrection: 0,
    fillInTheBlank: 0,
    freeEntry: 0,
    snippetSelection: 0,
    textHighlight: 0,
    graphing: 0,
    itemBank: 0,
    ordering: 0
  };
}

const DIFFICULTY_BUCKETS: Record<number, keyof MonthBucket> = {
  1: 'easy',
  2: 'moderate',
  3: 'difficult'
};

const TYPE_BUCKETS: Record<string, keyof QuestionTypeCounts> = {
  'multiple-choice': 'multipleChoice',
  'free-response': 'freeResponse',
  'two-way': 'twoWay',
  'passage-correction': 'passageCorrection',
  'fill-in-the-blank': 'fillInTheBlank',
  'free-entry': 'freeEntry',
  'snippet-selection': 'snippetSelection',
  'text-highlight': 'textHighlight',
  'graph-contains': 'graphing',
  'item-bank': 'itemBank',
  ordering: 'ordering'
};

/**
 * Tallies published questions into the three shapes the metrics panel renders:
 * a bar per month, a difficulty split, and a bar per question type.
 */
export function summarizePublishedQuestions(questions: readonly MetricsQuestion[]) {
  const publishedQuestionDates = createYearObject();
  const questionDifficulties: QuestionDifficulties = {all: 0, easy: 0, moderate: 0, difficult: 0};
  const questionTypes = createTypeObject();

  questions.forEach((question) => {
    questionDifficulties.all += 1;
    questionTypes.all += 1;

    const difficulty = question.difficulty ? DIFFICULTY_BUCKETS[question.difficulty] : undefined;
    if (difficulty) {
      questionDifficulties[difficulty] += 1;
    }

    const type = question.questionType ? TYPE_BUCKETS[question.questionType] : undefined;
    if (type) {
      questionTypes[type] += 1;
    }

    // A question with no publish date can still count toward the totals above,
    // but there is no bar to put it in.
    if (!question.publishedAt) {
      return;
    }
    const month = publishedQuestionDates[moment(question.publishedAt).format('MMM')];
    if (!month) {
      return;
    }
    month.count += 1;
    if (difficulty) {
      month[difficulty] += 1;
    }
  });

  return {publishedQuestionDates, questionDifficulties, questionTypes};
}
